import React from 'react';
import * as R from 'ramda';
import { arrayOf, bool, number, shape, string } from 'prop-types';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';

// Components
import { Box, Tooltip as MuiTooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

const StackedBarChart = props => {
  const { averageLoadTime, barSize, chartType, config, locationDetails } = props;
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up('md'), { noSsr: true });

  const renderBars = () => {
    // Config is passed from parent, in this case RackWaitTimes
    const bars = config.map(({ dataKey, barFillColor, stackId }) => {
      // The dataKey corresponds to a key from the data object
      // In order to create a STACKED Bar Chart, the stackId for all bars has to be the same
      return (
        <Bar
          barSize={barSize}
          dataKey={dataKey}
          fill={barFillColor}
          key={dataKey}
          stackId={stackId}
        />
      );
    });
    return bars;
  };

  const getBarDomain = waitTimes => {
    const maxWaitTime = waitTimes.reduce((prev, current) =>
      prev.minutes <= current.minutes ? current : prev
    );
    return R.cond([
      [R.lt(R.__, 30), R.always([5, 10, 15, 20, 25, 30])],
      [R.both(R.gt(R.__, 30), R.lt(R.__, 60)), R.always([10, 20, 30, 40, 50, 60])],
      [R.T, R.always([20, 40, 60, 80, 100, 120])],
    ])(maxWaitTime.minutes);
  };

  // this function takes in the time coming from the BE and converts it to 1/2 digit time with a/p
  // this is used to create the labels for the X-Axis when viewing by 24hour scale option
  const formatChartTime = timeString => {
    // Times from the BE are strings length of 4 (2:00) or 5 (12:00)
    // All times need to be in xx:xx:xx for this function to work (line below)
    const formattedTime = timeString.length === 4 ? `0${timeString}:00` : `${timeString}:00`;
    const currentDateString = new Date().toISOString().slice(0, 11);
    const timeNeeded = `${currentDateString}${formattedTime}Z`;
    const formattedTimeLabel = format(utcToZonedTime(timeNeeded), 'h aaaaa').replace(' ', '');
    return formattedTimeLabel;
  };

  const dataForGraph = ({ currentWaitTime, dailyWaitTimes, hourlyWaitTimes, locationId, name }) => {
    const newData =
      chartType === 'hourly'
        ? hourlyWaitTimes.map((time, index) => ({
            averageLoadTime,
            averageWaitTime: index !== 0 ? time.minutes : null,
            currentWaitTime: index === 0 ? currentWaitTime : null,
            key: formatChartTime(time.key),
            locationId,
            name,
          }))
        : dailyWaitTimes.map((time, index) => ({
            averageLoadTime,
            averageWaitTime: index !== 0 ? time.minutes : null,
            currentWaitTime: index === 0 ? currentWaitTime : null,
            key: time.key.slice(0, 3),
            locationId,
            name,
          }));
    return newData;
  };

  const getTooltipText = (label, payload) => {
    const formattedAmPm = label.slice(-1) === 'a' ? 'AM' : 'PM';
    const formattedLabel =
      chartType === 'hourly' ? `${label.substring(0, label.length - 1)}:00` : label;
    return `${formattedLabel} ${chartType === 'hourly' ? formattedAmPm : ''} ${
      payload[0].value
    } mins`;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload?.length) {
      return (
        <MuiTooltip enterTouchDelay={10} placement="top" title={label}>
          <Box
            alignItems="center"
            bgcolor="text.secondary"
            borderRadius="4px"
            display="flex"
            height={24}
            justifyContent="center"
            px={2}
            py={0.5}
          >
            <Typography sx={{ color: 'common.white', fontWeight: 100 }} variant="caption">
              {getTooltipText(label, payload)}
            </Typography>
          </Box>
        </MuiTooltip>
      );
    }
    return null;
  };

  CustomTooltip.propTypes = {
    active: bool,
    label: string,
    payload: arrayOf(shape({})),
  };

  CustomTooltip.defaultProps = {
    active: null,
    label: null,
    payload: null,
  };

  return (
    // eslint-disable-next-line sort-keys-fix/sort-keys-fix
    <Box p={{ xs: 0, md: 2 }} width="100%">
      <ResponsiveContainer height={!mdUp ? 250 : 400} width="100%">
        <ComposedChart
          // main chart data array (must be an array)
          data={dataForGraph(locationDetails)}
          // margin needed so top YAxis label doesnt get cutoff
          margin={{ top: 8 }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          {/* The dataKey corresponds to a key from the data object */}
          <XAxis
            dataKey="key"
            // this check is so the axis scale interval changes on mobile
            interval={!mdUp && chartType === 'hourly' ? 2 : 0}
            padding={{
              // padding check needed so the Daily bars dont expand outside of the graph area
              left: !!mdUp && chartType === 'daily' ? 50 : 20,
              right: !!mdUp && chartType === 'daily' ? 50 : 20,
              top: 20,
            }}
            scale="point"
            stroke="#ECECEC"
            tick={{ fill: 'rgba(0, 0, 0, 0.38)' }}
            tickLine={!mdUp}
            type="category"
          />
          {/* domain allows you to manually set the range of axis values */}
          <YAxis
            axisLine={false}
            domain={
              // [TODO] R.isNil check prevents graph from being max height if averageLoadTime is not present
              // refactor if averageLoadTime is always available
              averageLoadTime < 30 || R.isNil()
                ? [0, 30]
                : averageLoadTime < 60
                ? [0, 60]
                : [0, 120]
            }
            interval={0}
            tick={{ fill: 'rgba(0, 0, 0, 0.38)' }}
            tickCount={6}
            tickLine={false}
            ticks={
              chartType === 'hourly'
                ? getBarDomain(locationDetails?.hourlyWaitTimes)
                : getBarDomain(locationDetails?.dailyWaitTimes)
            }
            unit=" mins"
          />
          <Tooltip content={<CustomTooltip />} coordinate={{ x: 100, y: 140 }} cursor={false} />
          {renderBars()}
          {averageLoadTime && (
            <Line
              activeDot={false}
              dataKey="averageLoadTime"
              dot={false}
              stroke={theme.palette.success.dark}
              type="monotone"
              width="100%"
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
};

StackedBarChart.propTypes = {
  averageLoadTime: number,
  barSize: number.isRequired,
  chartType: string.isRequired,
  config: arrayOf(shape({})).isRequired,
  locationDetails: shape({}).isRequired,
};

StackedBarChart.defaultProps = {
  averageLoadTime: null,
};

export default StackedBarChart;
